import NodeCache from 'node-cache';
import { PlanType, PLAN_LIMITS, ApiError, RateLimits } from '../types';
import { PaymentVerificationService } from '../services/PaymentVerificationService';

interface RateLimitState {
  minute: { count: number; reset: number };
  hour: { count: number; reset: number };
  day: { count: number; reset: number };
}

export class RateLimiter {
  private cache: NodeCache;
  private planType: PlanType;
  private paymentService: PaymentVerificationService;

  constructor(planType: PlanType = PlanType.FREE, paymentService?: PaymentVerificationService) {
    this.cache = new NodeCache({ stdTTL: 86400 }); // 24 hour default TTL
    this.planType = planType;
    this.paymentService = paymentService || new PaymentVerificationService();
  }

  async checkLimit(userId: string): Promise<{ allowed: boolean; retryAfter?: number }> {
    // Check if user has an active paid plan
    const activePlan = this.paymentService.getPlanForApiKey(userId);
    const limits = PLAN_LIMITS[activePlan];
    
    // Enterprise has no limits
    if (activePlan === PlanType.ENTERPRISE) {
      return { allowed: true };
    }

    const now = Date.now();
    const state = this.getOrCreateState(userId, now);

    // Reset counters if needed
    if (now > state.minute.reset) {
      state.minute = { count: 0, reset: now + 60 * 1000 };
    }
    if (now > state.hour.reset) {
      state.hour = { count: 0, reset: now + 60 * 60 * 1000 };
    }
    if (now > state.day.reset) {
      state.day = { count: 0, reset: now + 24 * 60 * 60 * 1000 };
    }

    // Check limits
    if (state.minute.count >= limits.requests.perMinute) {
      return {
        allowed: false,
        retryAfter: Math.ceil((state.minute.reset - now) / 1000)
      };
    }
    if (state.hour.count >= limits.requests.perHour) {
      return {
        allowed: false,
        retryAfter: Math.ceil((state.hour.reset - now) / 1000)
      };
    }
    if (state.day.count >= limits.requests.perDay) {
      return {
        allowed: false,
        retryAfter: Math.ceil((state.day.reset - now) / 1000)
      };
    }

    // Increment counters
    state.minute.count++;
    state.hour.count++;
    state.day.count++;

    this.cache.set(userId, state);

    return { allowed: true };
  }

  async checkCapsuleLimit(userId: string): Promise<{ allowed: boolean; remaining: number }> {
    const activePlan = this.paymentService.getPlanForApiKey(userId);
    const limits = PLAN_LIMITS[activePlan];
    
    if (activePlan === PlanType.ENTERPRISE) {
      return { allowed: true, remaining: -1 };
    }

    const monthKey = `capsules:${userId}:${new Date().toISOString().slice(0, 7)}`;
    const count = this.cache.get<number>(monthKey) || 0;

    if (count >= limits.capsules.maxPerMonth) {
      return { allowed: false, remaining: 0 };
    }

    return { allowed: true, remaining: limits.capsules.maxPerMonth - count };
  }

  async incrementCapsuleCount(userId: string): Promise<void> {
    const monthKey = `capsules:${userId}:${new Date().toISOString().slice(0, 7)}`;
    const count = this.cache.get<number>(monthKey) || 0;
    this.cache.set(monthKey, count + 1, 30 * 24 * 60 * 60); // 30 days TTL
  }

  async checkStorageLimit(userId: string, sizeInMB: number): Promise<{ allowed: boolean; remaining: number }> {
    const activePlan = this.paymentService.getPlanForApiKey(userId);
    const limits = PLAN_LIMITS[activePlan];
    
    if (activePlan === PlanType.ENTERPRISE) {
      return { allowed: true, remaining: -1 };
    }

    const storageKey = `storage:${userId}`;
    const used = this.cache.get<number>(storageKey) || 0;
    const newTotal = used + sizeInMB;

    if (newTotal > limits.capsules.maxStorage) {
      return { allowed: false, remaining: limits.capsules.maxStorage - used };
    }

    return { allowed: true, remaining: limits.capsules.maxStorage - newTotal };
  }

  async incrementStorage(userId: string, sizeInMB: number): Promise<void> {
    const storageKey = `storage:${userId}`;
    const used = this.cache.get<number>(storageKey) || 0;
    this.cache.set(storageKey, used + sizeInMB);
  }

  getRemainingLimits(userId: string): RateLimits {
    const activePlan = this.paymentService.getPlanForApiKey(userId);
    const limits = PLAN_LIMITS[activePlan];
    
    if (activePlan === PlanType.ENTERPRISE) {
      return PLAN_LIMITS[PlanType.ENTERPRISE];
    }

    const now = Date.now();
    const state = this.getOrCreateState(userId, now);
    const monthKey = `capsules:${userId}:${new Date().toISOString().slice(0, 7)}`;
    const storageKey = `storage:${userId}`;

    const capsuleCount = this.cache.get<number>(monthKey) || 0;
    const storageUsed = this.cache.get<number>(storageKey) || 0;

    // Create a custom RateLimits object with current usage
    return {
      requests: {
        perMinute: Math.max(0, limits.requests.perMinute - state.minute.count),
        perHour: Math.max(0, limits.requests.perHour - state.hour.count),
        perDay: Math.max(0, limits.requests.perDay - state.day.count)
      },
      capsules: {
        maxPerMonth: Math.max(0, limits.capsules.maxPerMonth - capsuleCount),
        maxStorage: Math.max(0, limits.capsules.maxStorage - storageUsed)
      },
      features: limits.features
    };
  }

  changePlan(newPlan: PlanType): void {
    this.planType = newPlan;
  }

  reset(userId: string): void {
    this.cache.del(userId);
    this.cache.del(`capsules:${userId}:${new Date().toISOString().slice(0, 7)}`);
    this.cache.del(`storage:${userId}`);
  }

  getPlanInfo(userId: string): { plan: PlanType; daysRemaining: number; isActive: boolean } {
    const plan = this.paymentService.getPlanForApiKey(userId);
    const address = this.getAddressForApiKey(userId);
    const daysRemaining = address ? this.paymentService.getDaysUntilExpiry(address) : 0;
    const isActive = address ? this.paymentService.isPlanActive(address) : false;

    return {
      plan,
      daysRemaining,
      isActive
    };
  }

  private getAddressForApiKey(apiKey: string): string | undefined {
    // This is a simple implementation - in production, you'd query a database
    // For now, we'll use a reverse lookup from the payment service
    // This would need to be implemented properly with a database
    return undefined;
  }

  private getOrCreateState(userId: string, now: number): RateLimitState {
    let state = this.cache.get<RateLimitState>(userId);
    
    if (!state) {
      state = {
        minute: { count: 0, reset: now + 60 * 1000 },
        hour: { count: 0, reset: now + 60 * 60 * 1000 },
        day: { count: 0, reset: now + 24 * 60 * 60 * 1000 }
      };
      this.cache.set(userId, state);
    }

    return state;
  }

  // Middleware function for Express-like frameworks
  middleware() {
    return async (req: any, res: any, next: any) => {
      const userId = req.headers['x-api-key'] || 'anonymous';
      const { allowed, retryAfter } = await this.checkLimit(userId);

      if (!allowed) {
        res.status(429).json({
          success: false,
          error: {
            code: 'RATE_LIMIT_EXCEEDED',
            message: 'Too many requests',
            details: { retryAfter }
          }
        });
        return;
      }

      // Add rate limit headers
      const activePlan = this.paymentService.getPlanForApiKey(userId);
      const remaining = this.getRemainingLimits(userId);
      res.setHeader('X-RateLimit-Limit', PLAN_LIMITS[activePlan].requests.perMinute);
      res.setHeader('X-RateLimit-Remaining', remaining.requests.perMinute);
      res.setHeader('X-RateLimit-Reset', new Date(Date.now() + 60 * 1000).toISOString());
      res.setHeader('X-Plan-Type', activePlan);

      next();
    };
  }
} 